import { computed, onMounted, onUnmounted, readonly, ref } from 'vue';

import { NETWORK_CHECK_TIMEOUT, NETWORK_DEBOUNCE_DELAY } from '../constants.js';
import type { ConnectionType, EffectiveConnectionType, NetworkStatus } from '../types.js';

/**
 * Extended NetworkInformation interface (not all browsers support all properties)
 */
interface NetworkInformation extends EventTarget {
  readonly type?: ConnectionType;
  readonly effectiveType?: EffectiveConnectionType;
  readonly downlink?: number;
  readonly rtt?: number;
  readonly saveData?: boolean;
  onchange?: EventListener;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

// Module-level singleton state for shared network status
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);
const connectionType = ref<ConnectionType>('unknown');
const effectiveType = ref<EffectiveConnectionType | undefined>(undefined);
const downlink = ref<number | undefined>(undefined);
const rtt = ref<number | undefined>(undefined);
const saveData = ref<boolean | undefined>(undefined);
const lastChecked = ref<number>(Date.now());
const isInitialized = ref(false);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let listenerCount = 0;

/**
 * Updates network information from Navigator.connection API
 */
function updateNetworkInfo(): void {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (connection) {
    connectionType.value = connection.type ?? 'unknown';
    effectiveType.value = connection.effectiveType;
    downlink.value = connection.downlink;
    rtt.value = connection.rtt;
    saveData.value = connection.saveData;
  } else {
    connectionType.value = isOnline.value ? 'unknown' : 'none';
  }

  lastChecked.value = Date.now();
}

/**
 * Debounced handler for online/offline events
 */
function handleNetworkChange(online: boolean): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    isOnline.value = online;
    updateNetworkInfo();
  }, NETWORK_DEBOUNCE_DELAY);
}

/**
 * Event handlers
 */
function handleOnline(): void {
  handleNetworkChange(true);
}

function handleOffline(): void {
  handleNetworkChange(false);
}

function handleConnectionChange(): void {
  updateNetworkInfo();
}

/**
 * Composable for tracking network connectivity status
 *
 * Uses the Navigator.onLine API and optionally Navigator.connection
 * for more detailed network information.
 *
 * @example
 * ```typescript
 * const { isOnline, connectionType, checkConnection } = useNetworkStatus()
 *
 * watch(isOnline, (online) => {
 *   if (online) {
 *     console.log('Back online!')
 *   }
 * })
 * ```
 */
export function useNetworkStatus() {
  /**
   * Initializes event listeners (only once across all instances)
   */
  function initListeners(): void {
    if (typeof window === 'undefined') return;

    listenerCount++;

    if (!isInitialized.value) {
      // Initialize current state
      isOnline.value = navigator.onLine;
      updateNetworkInfo();

      // Add event listeners
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      // Add connection change listener if available
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        connection.addEventListener('change', handleConnectionChange);
      }

      isInitialized.value = true;
    }
  }

  /**
   * Cleans up event listeners (only when last instance unmounts)
   */
  function cleanupListeners(): void {
    if (typeof window === 'undefined') return;

    listenerCount--;

    if (listenerCount === 0 && isInitialized.value) {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }

      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }

      isInitialized.value = false;
    }
  }

  /**
   * Performs an active connectivity check by attempting to fetch a resource
   */
  async function checkConnection(url?: string): Promise<boolean> {
    const checkUrl = url ?? '/favicon.ico';

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), NETWORK_CHECK_TIMEOUT);

      const response = await fetch(checkUrl, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const online = response.ok;
      if (online !== isOnline.value) {
        isOnline.value = online;
        updateNetworkInfo();
      }

      return online;
    } catch {
      if (isOnline.value) {
        isOnline.value = false;
        updateNetworkInfo();
      }
      return false;
    }
  }

  // Computed properties
  const networkStatus = computed<NetworkStatus>(() => ({
    isOnline: isOnline.value,
    connectionType: connectionType.value,
    effectiveType: effectiveType.value,
    downlink: downlink.value,
    rtt: rtt.value,
    saveData: saveData.value,
  }));

  const isSlowConnection = computed(() => {
    if (!effectiveType.value) return false;
    return effectiveType.value === 'slow-2g' || effectiveType.value === '2g';
  });

  const isFastConnection = computed(() => {
    if (!effectiveType.value) return true; // Assume fast if unknown
    return effectiveType.value === '4g';
  });

  // Lifecycle hooks
  onMounted(() => {
    initListeners();
  });

  onUnmounted(() => {
    cleanupListeners();
  });

  return {
    // State (readonly to prevent external mutations)
    isOnline: readonly(isOnline),
    connectionType: readonly(connectionType),
    effectiveType: readonly(effectiveType),
    downlink: readonly(downlink),
    rtt: readonly(rtt),
    saveData: readonly(saveData),
    lastChecked: readonly(lastChecked),

    // Computed
    networkStatus,
    isSlowConnection,
    isFastConnection,

    // Methods
    checkConnection,
    updateNetworkInfo,
  };
}
